import React from 'react';
import { motion } from 'framer-motion';
import Header from '../../layout/Header';
import Footer from '../../sections/Footer';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

const { 
  FiSmartphone, FiAlertTriangle, FiShield, FiArrowLeft,
  FiCheck, FiX, FiMonitor, FiLock, FiPhoneCall,
  FiCreditCard, FiGlobe, FiUserX, FiUser, FiMail, FiCalendar
} = FiIcons;

const TechSupportScamsPage = () => {
  // Common tactics used in tech support scams
  const commonTactics = [
    "Pop-up alerts claiming your computer is infected with viruses",
    "Unsolicited calls from people claiming to be from Microsoft, Apple, or other tech companies",
    "Warnings that your computer's license has expired or is compromised",
    "Offering to fix non-existent problems for a fee",
    "Requesting remote access to your computer to 'diagnose' issues",
    "Creating fake error messages or system warnings on websites",
    "Asking for payment in gift cards or wire transfers"
  ];

  // Protection tips
  const protectionTips = [
    "Remember that legitimate tech companies don't make unsolicited calls about computer problems",
    "Never give remote access to your computer to someone who contacts you unexpectedly",
    "Ignore pop-up messages that tell you to call a number for tech support",
    "Use up-to-date antivirus software and enable pop-up blockers",
    "If you need tech support, contact companies through their official website",
    "Don't trust caller ID—phone numbers can be spoofed",
    "Be skeptical of anyone creating urgency or fear about your device's security"
  ];

  // Victim recovery steps
  const recoverySteps = [
    "Disconnect from the internet immediately to prevent further access",
    "Change all your passwords from a different, secure device",
    "Run a full system scan with legitimate antivirus software",
    "Uninstall any applications the scammer may have installed",
    "Contact your bank if you shared payment information or made a payment",
    "Monitor your accounts for suspicious activity",
    "Report the scam to the FTC at reportfraud.ftc.gov"
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1551808525-51a94da548ce?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
            alt="Tech Support Scam Concept"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-purple-900/80 to-gray-900/90"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link 
              to="/types-of-scams" 
              className="inline-flex items-center text-gray-400 hover:text-[#ffc700] mb-6 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Back to All Scam Types
            </Link>

            <div className="flex items-center gap-4 mb-4">
              <div className="bg-gradient-to-br from-purple-500 to-indigo-500 p-4 rounded-full">
                <SafeIcon icon={FiSmartphone} className="text-2xl text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-500 to-indigo-500 bg-clip-text text-transparent">
                Tech Support Scams
              </h1>
            </div>

            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
              <SafeIcon icon={FiAlertTriangle} className="text-purple-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-purple-400">High-Risk Scam Alert</h3>
                <p className="text-gray-300">
                  Tech support scams affect millions of people each year, with reported losses of over $347 million annually and an average loss of $500 per victim.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-16">
            {/* Overview Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-purple-500 to-indigo-500 bg-clip-text text-transparent">
                  What Are Tech Support Scams?
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Tech support scams occur when fraudsters impersonate IT technicians from well-known companies like Microsoft, Apple, or Google to trick you into believing your computer has a serious problem. These scammers aim to gain remote access to your device, steal personal information, or charge you for unnecessary and often harmful "repair services."
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  These scams typically begin with unsolicited phone calls, pop-up warnings, or fake error messages that create a false sense of urgency about your device's security. The scammers use technical jargon and scare tactics to convince victims that immediate action is needed, targeting less tech-savvy individuals who may not recognize the warning signs.
                </p>
                
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-purple-500 mb-2">$347M</div>
                    <p className="text-gray-400">annual reported losses</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-purple-500 mb-2">65%</div>
                    <p className="text-gray-400">of victims are over age 60</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-purple-500 mb-2">$500</div>
                    <p className="text-gray-400">average amount lost per victim</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* How It Works Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-purple-500 to-indigo-500 bg-clip-text text-transparent">
                  How Tech Support Scams Work
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <div className="space-y-10">
                  {/* Step 1 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-purple-500/20 p-4 rounded-full border border-purple-500/30 flex-shrink-0">
                      <SafeIcon icon={FiMonitor} className="text-2xl text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Initial Contact Through Pop-ups or Calls</h3>
                      <p className="text-gray-300 leading-relaxed">
                        The scam typically begins with either a pop-up warning on your computer screen claiming your device is infected or at risk, or through unsolicited phone calls where the caller claims to be from a reputable tech company. These pop-ups often feature alarming messages, flashing screens, and countdown timers to create panic. They may lock your browser and display a toll-free number to call for "immediate assistance."
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-purple-500/20 p-4 rounded-full border border-purple-500/30 flex-shrink-0">
                      <SafeIcon icon={FiUserX} className="text-2xl text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Creating Fear and Urgency</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Once they have your attention, scammers use technical jargon and scare tactics to convince you that your computer has serious problems. They might claim your device is infected with malware, your license has expired, or your personal information is at risk. They often use official-sounding terms and company names to appear legitimate and may direct you to standard Windows utilities or event logs that show normal system messages but claim they're evidence of problems.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-purple-500/20 p-4 rounded-full border border-purple-500/30 flex-shrink-0">
                      <SafeIcon icon={FiLock} className="text-2xl text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Gaining Remote Access</h3>
                      <p className="text-gray-300 leading-relaxed">
                        The scammer will persuade you to download remote access software, which gives them complete control over your computer. They'll claim this is necessary to diagnose and fix the supposed problems. Popular remote access programs like TeamViewer, AnyDesk, or LogMeIn are legitimate tools but can be misused by scammers. Once they have access, they can view your files, install malware, or manipulate your system to "demonstrate" problems.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-purple-500/20 p-4 rounded-full border border-purple-500/30 flex-shrink-0">
                      <SafeIcon icon={FiCreditCard} className="text-2xl text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Charging for Fake Solutions</h3>
                      <p className="text-gray-300 leading-relaxed">
                        After convincing you that your computer has serious issues, the scammer offers to fix the non-existent problems for a fee. They may request payment through credit cards, gift cards, wire transfers, or cryptocurrency. The charges can range from $100 to several thousand dollars for "lifetime technical support" or "security software." If you pay, they might perform some meaningless actions on your computer to make it seem like they've fixed something, but in reality, they've done nothing beneficial and may have installed actual malware or backdoor access.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-10">
                  <h3 className="text-xl font-bold text-white mb-4">Common Tactics & Red Flags</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {commonTactics.map((tactic, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <SafeIcon icon={FiAlertTriangle} className="text-purple-500 mt-1 flex-shrink-0" />
                        <p className="text-gray-300">{tactic}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Protection Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-purple-500 to-indigo-500 bg-clip-text text-transparent">
                  How to Protect Yourself
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {protectionTips.map((tip, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                      <p className="text-gray-300">{tip}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiPhoneCall} className="text-purple-500" />
                    Linda's Quick Tips for Handling Tech Support Calls
                  </h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    "Listen to me, sweetheart. Microsoft isn't calling you about viruses on your computer. Apple isn't monitoring your iPhone for malware. That's not how it works! If someone calls you claiming your computer has a virus, hang up immediately. And those scary pop-up messages telling you to call a number? Close your browser or restart your computer. Don't call the number, don't give them access to your computer, and for heaven's sake, don't give them your credit card!"
                  </p>
                  <p className="text-[#ffc700] font-medium">- Linda Roberts</p>
                </div>
              </div>
            </motion.div>

            {/* What to Do If You're a Victim */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-purple-500 to-indigo-500 bg-clip-text text-transparent">
                  What to Do If You're a Victim
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  If you've already interacted with a tech support scammer or allowed someone remote access to your computer, take these steps immediately to secure your device and personal information:
                </p>

                <div className="space-y-4 mb-8">
                  {recoverySteps.map((step, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-purple-500 to-indigo-500 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white text-sm font-bold">{index + 1}</span>
                      </div>
                      <p className="text-gray-300">{step}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4">Important Resources</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiGlobe} className="text-purple-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">Federal Trade Commission</p>
                        <a href="https://reportfraud.ftc.gov" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">reportfraud.ftc.gov</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiGlobe} className="text-purple-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">FBI Internet Crime Complaint Center</p>
                        <a href="https://www.ic3.gov" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">www.ic3.gov</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiPhoneCall} className="text-purple-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">AVAH Outreach Group</p>
                        <Link to="/scam-assistance" className="text-purple-400 hover:underline">Get Free Victim Support</Link>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiGlobe} className="text-purple-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">Microsoft's Tech Support Scam Info</p>
                        <a href="https://support.microsoft.com/en-us/topic/protect-yourself-from-tech-support-scams-2ebf91bd-f94c-2a8a-e541-f5c800d18435" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">support.microsoft.com</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Next Steps CTA */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-purple-900 to-gray-900 p-8 rounded-2xl border border-purple-500/30 relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-500/10 rounded-full translate-y-12 -translate-x-12"></div>

              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-4 text-center">
                  Continue Learning About Common Scams
                </h3>
                <p className="text-gray-300 text-center max-w-2xl mx-auto mb-8">
                  Knowledge is your best defense against scammers. Explore these related scam types to better protect yourself and your loved ones.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <Link
                    to="/scams/phishing-schemes"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiMail} className="text-2xl text-purple-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Phishing Schemes</p>
                  </Link>
                  <Link
                    to="/scams/impersonation-scams"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiUser} className="text-2xl text-purple-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Impersonation Scams</p>
                  </Link>
                  <Link
                    to="/scams/subscription-scams"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiCalendar} className="text-2xl text-purple-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Subscription Scams</p>
                  </Link>
                </div>

                <div className="mt-8 text-center">
                  <Link
                    to="/types-of-scams"
                    className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-3 shadow-lg"
                  >
                    <SafeIcon icon={FiShield} className="text-xl" />
                    View All Scam Types
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TechSupportScamsPage;